import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AddressItem, AddressProps } from '@partsbadger/library';
import { Button, Modal, Radio, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface IProps {
    currentAddress: AddressProps | null;
    addresses: AddressProps[];
    type: 'Billing' | 'Shipping' | 'Billing and Shipping';
    onAddNew?: () => void;
    hideEditButton?: boolean;
    onSelect?: (address: AddressProps) => void;
    onEdit?: (address: AddressProps) => void;
}

const toNum = (v: unknown): number | null => {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const ShowDefault: React.FC<IProps> = ({
    currentAddress,
    addresses,
    type,
    onAddNew,
    hideEditButton,
    onSelect,
    onEdit,
}) => {
    const [selectOpen, setSelectOpen] = useState(false);
    const addBtnRef = useRef<HTMLButtonElement>(null);

    const noneSaved = !addresses || addresses.length === 0;
    const noCurrent = !currentAddress || !currentAddress.street;

    useEffect(() => {
        if (noCurrent && noneSaved && addBtnRef.current) addBtnRef.current.focus();
    }, [noCurrent, noneSaved]);

    const [selectedId, setSelectedId] = useState<number | null>(null);

    useEffect(() => {
        if (!selectOpen) return;
        setSelectedId(toNum(currentAddress?.id));
    }, [selectOpen, currentAddress?.id]);

    const selectedAddress = useMemo(() => {
        const id = selectedId;
        if (id == null) return null;
        return addresses.find(a => toNum(a.id) === id) ?? null;
    }, [addresses, selectedId]);

    if (noCurrent && noneSaved) {
        return (
            <div>
                <Text type="secondary" style={{ display: 'inline-block', marginBottom: 8 }}>
                    Add a {type.toLowerCase()} address to continue.
                </Text>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    block
                    onClick={onAddNew}
                    ref={addBtnRef}
                    style={{ height: 44 }}
                >
                    Add {type} Address
                </Button>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-row justify-between items-start w-full">
                {currentAddress && <AddressItem address={currentAddress} />}

                {!hideEditButton && (
                    <div className="address-actions-container" style={{ marginLeft: 12 }}>
                        <Button type="link" onClick={() => setSelectOpen(true)}>
                            Change Address
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={onAddNew}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            Add New
                        </Button>
                    </div>
                )}
            </div>

            <Modal
                title={`Select ${type} Address`}
                open={selectOpen}
                onCancel={() => setSelectOpen(false)}
                destroyOnClose
                footer={[
                    <Button key="cancel" onClick={() => setSelectOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="use"
                        type="primary"
                        disabled={!selectedAddress}
                        onClick={() => {
                            if (!selectedAddress) return;
                            onSelect?.(selectedAddress);
                            setSelectOpen(false);
                        }}
                    >
                        Use this Address
                    </Button>,
                ]}
            >
                {noneSaved ? (
                    <Text type="secondary">No saved addresses found.</Text>
                ) : (
                    <Radio.Group
                        style={{ width: '100%' }}
                        value={selectedId ?? undefined}
                        onChange={e => setSelectedId(toNum(e.target.value))}
                    >
                        <div className="flex flex-col gap-3">
                            {addresses.map(a => (
                                <Radio key={a.id} value={toNum(a.id) ?? undefined} style={{ width: '100%' }}>
                                    <div style={{ marginLeft: 8 }}>
                                        <div className="flex justify-between items-start gap-3">
                                            <div style={{ flex: 1 }}>
                                                <AddressItem address={a} />
                                            </div>

                                            {!hideEditButton && (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    onMouseDown={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation(); // prevents the Radio from selecting on mousedown
                                                    }}
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation(); // prevents bubbling into Radio label click
                                                        setSelectOpen(false);
                                                        onEdit?.(a);
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </Radio>
                            ))}
                        </div>
                    </Radio.Group>
                )}
            </Modal>
        </>
    );
};

export default ShowDefault;
